import { useCallback, useEffect, useState } from "react";
import { CircleNotch, Warning } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { StatsFilters } from "@/components/stats/StatsFilters";
import { KpiGrid } from "@/components/stats/KpiGrid";
import { RevenueOccupancyChart, ChannelsChart, WeekdayChart, DistributionChart } from "@/components/stats/StatsCharts";
import { BreakdownTable } from "@/components/stats/BreakdownTable";
import { OccupancyHeatmap } from "@/components/stats/OccupancyHeatmap";
import { presetRange } from "@/components/stats/statsUtils";

function useStatsFilters(activePropertyId) {
  const [f, setF] = useState(() => {
    const [from, to] = presetRange("this_month");
    return { preset: "this_month", from, to, property_id: activePropertyId || "", room_type_id: "", unit_id: "", channel: "", basis: "stay", revenue_mode: "gross" };
  });
  const set = (patch) => setF((prev) => {
    const next = { ...prev, ...patch };
    if (patch.preset && patch.preset !== "custom") [next.from, next.to] = presetRange(patch.preset);
    return next;
  });
  return [f, set];
}

const hrDate = (iso) => { const [y, m, d] = iso.split("-"); return `${Number(d)}.${Number(m)}.${y}.`; };

function Skeleton() {
  return (
    <div className="space-y-4" data-testid="stats-loading">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => <div key={i} className="rw-skeleton h-24 rounded-lg" />)}
      </div>
      <div className="rw-skeleton h-80 rounded-lg" />
    </div>
  );
}

export default function StatisticsPage() {
  const { properties, activePropertyId } = useProperty();
  const [f, set] = useStatsFilters(activePropertyId);
  useEffect(() => { set({ property_id: activePropertyId || "", room_type_id: "", unit_id: "" }); }, [activePropertyId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [roomTypes, setRoomTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/room-types").then((r) => setRoomTypes(r.data || [])).catch(() => {});
    api.get("/units").then((r) => setUnits((r.data || []).filter((u) => u.active !== false))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!f.from || !f.to) return;
    setLoading(true); setErr("");
    try {
      const params = { date_from: f.from, date_to: f.to, basis: f.basis, revenue_mode: f.revenue_mode };
      ["property_id", "room_type_id", "unit_id", "channel"].forEach((k) => { if (f[k]) params[k] = f[k]; });
      const { data: d } = await api.get("/statistics", { params });
      setData(d);
    } catch (e) { setErr(formatApiError(e)); } finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-[1500px] space-y-4 p-3 sm:p-6" data-testid="statistics-page">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          {data && <p className="text-xs text-muted-foreground sm:text-sm" data-testid="stats-range-label">{hrDate(data.range.from)} – {hrDate(data.range.to)} · usporedba s {hrDate(data.range.prev_from)} – {hrDate(data.range.prev_to)} · {data.unit_count} jedinica</p>}
        </div>
        {loading && data && <CircleNotch size={18} className="animate-spin text-muted-foreground" />}
      </div>
      <StatsFilters f={f} set={set} properties={properties || []} roomTypes={roomTypes} units={units} />
      {err && <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" data-testid="stats-error"><Warning size={16} /> {err}</div>}
      {!data && loading && <Skeleton />}
      {data && (
        <div className={`space-y-4 transition-opacity duration-200 ${loading ? "opacity-60" : "opacity-100"}`}>
          <KpiGrid kpis={data.kpis} prev={data.prev_kpis} deltas={data.deltas} />
          <RevenueOccupancyChart series={data.series} gran={data.range.granularity} />
          <div className="grid gap-4 lg:grid-cols-2">
            <ChannelsChart channels={data.channels} />
            <WeekdayChart weekdays={data.weekdays} />
          </div>
          <BreakdownTable breakdown={data.breakdown} range={data.range} />
          <OccupancyHeatmap heatmap={data.heatmap} gran={data.range.granularity} />
          <div className="grid gap-4 lg:grid-cols-2">
            <DistributionChart title="Duljina boravka" subtitle="Broj rezervacija po broju noći" data={data.los} testId="stats-chart-los" />
            <DistributionChart title="Rezervirano unaprijed" subtitle="Dana od rezervacije do dolaska" data={data.lead} color="#FF990A" testId="stats-chart-lead" />
          </div>
        </div>
      )}
    </div>
  );
}
